import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  Stack,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import * as api from '../../services/api';
import { useI18n } from '../../i18n/useI18n';
import { rewriteToCdn } from '../../utils/cdn';

interface Machine {
  _id: string;
  machineCode: string;
  randomCode: string;
  machineName: string;
  isActive: boolean;
  isAppRunning?: boolean;
  appPlatform?: 'windows' | 'android' | null;
  appProduct?: 'lite' | 'mono' | 'pro' | null;
  notes: string;
  userId?: {
    _id: string;
    username: string;
    email: string;
    avatarUrl?: string;
  };
  createdAt: string;
  updatedAt: string;
}

const appProductLabelColor = (product?: string | null) => {
  const p = String(product || '').trim().toLowerCase();
  if (p === 'lite') return '#0284c7';
  if (p === 'mono') return '#4b5563';
  if (p === 'pro') return '#c2410c';
  return undefined;
};

const PAGE_SIZE = 10;

const gradientBorder =
  'linear-gradient(165deg, #ffffff 0%, #fff8fb 58%, #fff5f8 100%) padding-box, linear-gradient(135deg, rgba(255,139,167,0.95), rgba(244,143,177,0.55)) border-box';

const gradientFill = 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)';

const gradientFillHover = 'linear-gradient(135deg, #ff8ba7 0%, #f48fb1 52%, #bf5f82 100%)';

const DesktopList: React.FC = () => {
  const { admin: a, pages: t, formatMessage } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [updating, setUpdating] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);

  const openMachineDeviceMgmt = (machine: Machine) => {
    navigate(`/admin/desktops/${encodeURIComponent(machine.machineCode)}`);
  };

  const fetchMachines = useCallback(async (pageNum: number, append: boolean) => {
    if (append) {
      if (loadingMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      loadingMoreRef.current = false;
      setLoadingMore(false);
      setLoading(true);
    }

    try {
      const response = await api.getAdminMachines(pageNum, PAGE_SIZE);
      const incoming = response.machines || [];
      setMachines((prev) => {
        if (!append) return incoming;
        const seen = new Set(prev.map((m) => m._id));
        return [...prev, ...incoming.filter((m) => !seen.has(m._id))];
      });
      setTotal(response.pagination.total);
      setHasMore(pageNum < response.pagination.pages);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching machines:', error);
      toast.error('Failed to load machines');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchMachines(1, false);
  }, [fetchMachines]);

  useEffect(() => {
    if (loading) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMoreRef.current) {
          fetchMachines(page + 1, true);
        }
      },
      { rootMargin: '240px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, hasMore, page, fetchMachines]);

  const handleUpdateClick = (machine: Machine) => {
    setSelectedMachine(machine);
    setUpdateDialogOpen(true);
  };

  const handleUpdateClose = () => {
    setUpdateDialogOpen(false);
    setSelectedMachine(null);
  };

  const handleUpdateSubmit = async () => {
    if (!selectedMachine) return;

    try {
      setUpdating(true);
      const response = await api.updateMachineRandomCode(selectedMachine.machineCode);
      toast.success(response.message || 'Mã random đã được cập nhật thành công');
      fetchMachines(1, false);
      handleUpdateClose();
    } catch (error) {
      console.error('Error updating machine:', error);
      toast.error('Failed to update machine');
    } finally {
      setUpdating(false);
    }
  };

  const renderMachineTitle = (machine: Machine, fontSize = '0.9rem') => {
    const name = String(machine.machineName || '').trim();
    const code = machine.machineCode;

    if (!name) {
      return (
        <Typography
          component="span"
          fontWeight={800}
          sx={{
            fontSize,
            color: 'primary.main',
            fontFamily: 'ui-monospace, monospace',
            letterSpacing: '0.02em',
          }}
        >
          {code}
        </Typography>
      );
    }

    return (
      <Typography
        component="span"
        sx={{
          display: 'inline',
          lineHeight: 1.3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize,
        }}
      >
        <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>
          {name}
        </Box>
        <Box component="span" sx={{ mx: 0.6, fontWeight: 700, color: 'text.disabled' }}>
          -
        </Box>
        <Box
          component="span"
          sx={{
            fontWeight: 800,
            color: 'primary.main',
            fontFamily: 'ui-monospace, monospace',
            letterSpacing: '0.02em',
          }}
        >
          {code}
        </Box>
      </Typography>
    );
  };

  const productLabel = (machine: Machine) => {
    const p = String(machine.appProduct || '').trim().toLowerCase();
    if (!p) return null;
    return p.toUpperCase();
  };

  const usesRandomCode = (machine: Machine) =>
    String(machine.appProduct || '').trim().toLowerCase() !== 'mono';

  const renderTypeChips = (machine: Machine) => {
    const product = productLabel(machine);
    const productColor = appProductLabelColor(machine.appProduct);

    if (!product) {
      return (
        <Chip
          label={a.desktops.unknownType}
          size="small"
          variant="outlined"
          sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem' }}
        />
      );
    }

    return (
      <Chip
        label={product}
        size="small"
        sx={{
          height: 22,
          fontWeight: 800,
          fontSize: '0.68rem',
          bgcolor: productColor ? alpha(productColor, 0.14) : undefined,
          color: productColor || 'text.primary',
          border: '1px solid',
          borderColor: productColor ? alpha(productColor, 0.35) : 'divider',
        }}
      />
    );
  };

  const renderConnChip = (machine: Machine) => {
    const running = Boolean(machine.isAppRunning);
    return (
      <Chip
        label={running ? a.desktops.connected : a.desktops.disconnected}
        size="small"
        sx={{
          height: 22,
          fontWeight: 800,
          fontSize: '0.68rem',
          bgcolor: running ? alpha('#16a34a', 0.14) : alpha('#64748b', 0.1),
          color: running ? '#15803d' : '#64748b',
          border: '1px solid',
          borderColor: running ? alpha('#16a34a', 0.35) : alpha('#64748b', 0.25),
        }}
      />
    );
  };

  const renderUserCell = (machine: Machine, compact = false) => {
    if (!machine.userId) {
      return (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      );
    }
    const avatarSrc = machine.userId.avatarUrl
      ? rewriteToCdn(machine.userId.avatarUrl)
      : undefined;
    const initial = (machine.userId.username || '?').charAt(0).toUpperCase();

    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            p: '2px',
            borderRadius: '50%',
            flexShrink: 0,
            background: gradientFill,
            boxShadow: '0 4px 10px rgba(191, 95, 130, 0.22)',
          }}
        >
        <Avatar
          src={avatarSrc}
          alt={machine.userId.username}
          sx={{
            width: compact ? 26 : 32,
            height: compact ? 26 : 32,
            fontSize: compact ? '0.72rem' : '0.85rem',
            fontWeight: 800,
            bgcolor: '#ffe4ee',
            color: '#bf5f82',
            border: '2px solid #fff',
          }}
        >
          {!avatarSrc ? initial : null}
        </Avatar>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant={compact ? 'caption' : 'body2'}
            fontWeight={700}
            noWrap
            title={machine.userId.username}
            sx={compact ? { display: 'block', lineHeight: 1.35, color: 'text.secondary' } : undefined}
          >
            {machine.userId.username}
            {compact && machine.userId.email ? ` · ${machine.userId.email}` : ''}
          </Typography>
          {!compact && machine.userId.email && (
            <Typography variant="caption" color="text.secondary" noWrap display="block" title={machine.userId.email}>
              {machine.userId.email}
            </Typography>
          )}
        </Box>
      </Stack>
    );
  };

  if (loading && machines.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const headerBtnSx = {
    flexShrink: 0,
    textTransform: 'none' as const,
    fontWeight: 800,
    borderRadius: 999,
    minWidth: 44,
    width: 44,
    height: 44,
    p: 0,
    color: '#fff',
    background: gradientFill,
    boxShadow: '0 8px 16px rgba(191, 95, 130, 0.28)',
    '&:hover': { background: gradientFillHover },
  };

  const machineIconSx = {
    width: 40,
    height: 40,
    borderRadius: 2,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    background: gradientFill,
    boxShadow: '0 6px 14px rgba(191, 95, 130, 0.28)',
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.25}
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: 8,
          mb: { xs: 1.5, sm: 2.5 },
          mx: { xs: -1.5, sm: -3 },
          px: { xs: 1.5, sm: 3 },
          py: 1,
          bgcolor: 'background.default',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: '#fff',
              background: gradientFill,
              boxShadow: '0 6px 14px rgba(191, 95, 130, 0.28)',
            }}
          >
            <ComputerIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              fontWeight={800}
              sx={{
                fontSize: { xs: '1.2rem', sm: '1.75rem' },
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
                color: '#bf5f82',
              }}
            >
              {a.desktops.title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.15, fontWeight: 600, color: '#e06a88' }}>
              {formatMessage(a.desktops.totalMachines, { n: total })}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          startIcon={!isMobile ? <RefreshIcon /> : undefined}
          onClick={() => fetchMachines(1, false)}
          disabled={loading}
          sx={{
            ...headerBtnSx,
            ...(isMobile ? {} : { minWidth: 'auto', width: 'auto', px: 2 }),
          }}
          aria-label={a.desktops.refresh}
        >
          {isMobile ? <RefreshIcon /> : a.desktops.refresh}
        </Button>
      </Stack>

      {isMobile ? (
        <Stack spacing={1.25}>
          {machines.map((machine) => {
            const running = Boolean(machine.isAppRunning);
            const showRandom = usesRandomCode(machine);
            return (
              <Paper
                key={machine._id}
                elevation={0}
                onClick={() => openMachineDeviceMgmt(machine)}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: running ? '2px solid transparent' : '1px solid transparent',
                  background: running
                    ? 'linear-gradient(145deg, #f0fdf4 0%, #ffffff 60%) padding-box, linear-gradient(135deg, #4ade80, #16a34a) border-box'
                    : 'linear-gradient(145deg, #ffffff 0%, #fff8fb 100%) padding-box, linear-gradient(135deg, #ffd6e2, #f8bbd0) border-box',
                  boxShadow: running
                    ? '0 10px 24px rgba(22, 163, 74, 0.16)'
                    : '0 4px 14px rgba(244, 143, 177, 0.12)',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s',
                  '&:active': { opacity: 0.92 },
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <Box sx={machineIconSx}>
                    <ComputerIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {renderMachineTitle(machine)}
                    </Box>

                    <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                      {renderConnChip(machine)}
                      {renderTypeChips(machine)}
                    </Stack>

                    {machine.userId && (
                      <Box sx={{ mt: 0.75 }}>
                        {renderUserCell(machine, true)}
                      </Box>
                    )}
                  </Box>

                  {showRandom && (
                    <Tooltip title={a.desktops.updateRandomCode}>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateClick(machine);
                        }}
                        sx={{
                          flexShrink: 0,
                          color: '#e06a88',
                          borderRadius: 2,
                          backgroundImage: 'linear-gradient(135deg, rgba(255,193,212,0.7), rgba(255,139,167,0.35))',
                          '&:hover': {
                            color: '#fff',
                            backgroundImage: gradientFill,
                          },
                        }}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid transparent',
            background: gradientBorder,
            boxShadow: '0 14px 36px rgba(191, 95, 130, 0.08)',
            overflow: 'hidden',
          }}
        >
          <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHead>
              <TableRow
                sx={{
                  background: 'linear-gradient(135deg, #fff5f8 0%, #ffeef3 100%)',
                  '& th': {
                    fontWeight: 800,
                    fontSize: '0.8rem',
                    color: '#bf5f82',
                    bgcolor: 'transparent',
                    py: 1.5,
                    borderColor: alpha('#f48fb1', 0.28),
                  },
                }}
              >
                <TableCell>{a.desktops.colMachineName}</TableCell>
                <TableCell>{a.desktops.colType}</TableCell>
                <TableCell>{a.desktops.colStatus}</TableCell>
                <TableCell>{a.desktops.colUser}</TableCell>
                <TableCell>{a.desktops.colUpdatedAt}</TableCell>
                <TableCell align="right">{a.desktops.colActions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {machines.map((machine) => {
                const running = Boolean(machine.isAppRunning);
                const showRandom = usesRandomCode(machine);
                return (
                  <TableRow
                    key={machine._id}
                    hover
                    onClick={() => openMachineDeviceMgmt(machine)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: running ? alpha('#16a34a', 0.06) : undefined,
                      '& td': {
                        py: 1.5,
                        borderTop: running ? '2px solid #16a34a' : undefined,
                        borderBottom: running ? '2px solid #16a34a' : undefined,
                        borderColor: running ? '#16a34a' : alpha('#f48fb1', 0.22),
                      },
                      '& td:first-of-type': {
                        borderLeft: running ? '2px solid #16a34a' : undefined,
                      },
                      '& td:last-of-type': {
                        borderRight: running ? '2px solid #16a34a' : undefined,
                      },
                      '&:hover': { bgcolor: running ? alpha('#16a34a', 0.1) : alpha('#f48fb1', 0.06) },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box sx={{ ...machineIconSx, width: 36, height: 36 }}>
                          <ComputerIcon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          {renderMachineTitle(machine)}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{renderTypeChips(machine)}</TableCell>
                    <TableCell>{renderConnChip(machine)}</TableCell>
                    <TableCell>
                      {renderUserCell(machine)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(machine.updatedAt), 'dd/MM/yyyy HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {showRandom ? (
                        <Tooltip title={a.desktops.updateRandomCode}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateClick(machine);
                            }}
                            color="primary"
                            size="small"
                            sx={{
                              color: '#e06a88',
                              borderRadius: 2,
                              backgroundImage: 'linear-gradient(135deg, rgba(255,193,212,0.7), rgba(255,139,167,0.35))',
                              '&:hover': {
                                color: '#fff',
                                backgroundImage: gradientFill,
                              },
                            }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(hasMore || loadingMore) && machines.length > 0 && (
        <Box ref={sentinelRef} sx={{ py: 2 }}>
          {loadingMore ? (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Đang tải thêm...
              </Typography>
            </Stack>
          ) : (
            <Box sx={{ height: 1 }} />
          )}
        </Box>
      )}

      <Dialog
        open={updateDialogOpen}
        onClose={handleUpdateClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 2 },
            width: { xs: 'calc(100% - 32px)', sm: '100%' },
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid transparent',
            background:
              'linear-gradient(165deg, #ffffff 0%, #fff8fb 72%, #fff5f8 100%) padding-box, linear-gradient(135deg, rgba(255,139,167,0.95), rgba(244,143,177,0.55)) border-box',
            boxShadow: '0 24px 60px rgba(191, 95, 130, 0.18)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.05rem', sm: '1.2rem' },
            color: '#bf5f82',
            background: 'linear-gradient(135deg, #fff5f8 0%, #ffeef3 100%)',
            borderBottom: '1px solid',
            borderColor: alpha('#f48fb1', 0.35),
          }}
        >
          {a.desktops.dialogTitle}
        </DialogTitle>
        <DialogContent>
          {selectedMachine && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid transparent',
                background:
                  'linear-gradient(145deg, #fff7fb 0%, #ffffff 100%) padding-box, linear-gradient(135deg, #ffc1d4, #ff8ba7, #f48fb1) border-box',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                {a.desktops.dialogConfirm}
              </Typography>
              <Box sx={{ mb: 0.5 }}>
                {renderMachineTitle(selectedMachine)}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {a.desktops.currentRandomCode}:{' '}
                <Box component="strong" sx={{ color: 'primary.main' }}>
                  {selectedMachine.randomCode}
                </Box>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1,
            borderTop: '1px solid',
            borderColor: alpha('#f48fb1', 0.28),
            background: 'linear-gradient(180deg, #ffffff 0%, #fff8fb 100%)',
          }}
        >
          <Button
            onClick={handleUpdateClose}
            disabled={updating}
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999, color: '#e06a88' }}
          >
            {t.common.cancel}
          </Button>
          <Button
            onClick={handleUpdateSubmit}
            variant="contained"
            disabled={updating}
            fullWidth={isMobile}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 999,
              color: '#fff',
              background: gradientFill,
              boxShadow: '0 8px 16px rgba(191, 95, 130, 0.28)',
              '&:hover': { background: gradientFillHover },
            }}
          >
            {updating ? <CircularProgress size={20} color="inherit" /> : a.desktops.update}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DesktopList;
